import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Table, Dropdown, Button, Empty, Card, Avatar, Input, Select, Flex, Tag, Modal, message, Spin, Tooltip } from "antd";
import { MoreOutlined, DollarOutlined, TrophyOutlined, LineChartOutlined, PlusOutlined, SearchOutlined, FilterOutlined, CloseOutlined, ExclamationCircleOutlined, DeleteOutlined, EditOutlined, StopOutlined, EyeOutlined, FileProtectOutlined, SyncOutlined, CheckCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { API_CONST } from "../../../const";
import {
  TalentsHiredContainer,
  PageHeader,
  MetricsContainer,
  TabsContainer,
  TableContainer,
  EmptyState,
} from "./TalentsHired.style";

const { Option } = Select;

const TalentsHired = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("Active Talents");
  const [searchText, setSearchText] = useState("");
  const [filterRole, setFilterRole] = useState(null);
  const [filterLocation, setFilterLocation] = useState(null);
  const [filterJobId, setFilterJobId] = useState(searchParams.get("jobId") || null);
  const [hiringRecords, setHiringRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const jobIdParam = searchParams.get("jobId");
    if (jobIdParam) {
      setFilterJobId(jobIdParam);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchHiringRecords();
  }, []);

  const fetchHiringRecords = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_HIRING_RECORDS,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHiringRecords(response.data.hiringRecords || []);
    } catch (error) {
      console.error("Error fetching hiring records:", error);
      message.error("Failed to load hiring records");
    } finally {
      setLoading(false);
    }
  };

  const clearJobIdFilter = () => {
    setFilterJobId(null);
    setSearchParams({});
  };

  const activeTalents = hiringRecords.filter((record) => record.status === "active");
  const draftTalents = hiringRecords.filter((record) => record.status === "draft");
  const inactiveTalents = hiringRecords.filter((record) => record.status !== "active" && record.status !== "draft");

  const baseData = activeTab === "Active Talents" ? activeTalents : activeTab === "Pending Activation" ? draftTalents : inactiveTalents;

  const currentData = baseData.filter((record) => {
    const matchesSearch = 
      searchText === "" ||
      record.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      record.role?.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesRole = !filterRole || record.role === filterRole;
    const matchesLocation = !filterLocation || record.location === filterLocation;
    const matchesJobId = !filterJobId || record.jobId === filterJobId;

    return matchesSearch && matchesRole && matchesLocation && matchesJobId;
  });

  const calculateMetrics = () => {
    const activeMonthlyBilling = activeTalents.reduce(
      (sum, record) => sum + (parseFloat(record.clientRate) || 0),
      0
    );
    const totalBilled = activeTalents.reduce(
      (sum, record) => sum + (parseFloat(record.totalBilled) || 0),
      0
    );
    const avgPerTalent =
      activeTalents.length > 0
        ? activeMonthlyBilling / activeTalents.length
        : 0;

    return {
      activeMonthlyBilling,
      totalBilled,
      avgPerTalent,
      activeTalentsCount: activeTalents.length,
    };
  };

  const metrics = calculateMetrics();

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} Lac`;
    } else {
      return `₹${amount.toLocaleString("en-IN")}`;
    }
  };

  const handleTerminate = (record) => {
    Modal.confirm({
      title: "Terminate Contract",
      icon: <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />,
      content: (
        <div>
          <p>Are you sure you want to terminate this contract?</p>
          <div style={{ marginTop: 12, padding: 12, background: "#fff2f0", borderRadius: 6 }}>
            <p style={{ margin: 0, fontWeight: 500 }}>{record.name}</p>
            <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>{record.role}</p>
          </div>
        </div>
      ),
      okText: "Terminate",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.put(
            `${API_CONST.TERMINATE_HIRING_RECORD}/${record.id}`,
            { terminationReason: "Manual termination by admin" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Contract terminated successfully");
          fetchHiringRecords();
        } catch (error) {
          console.error("Error terminating contract:", error);
          message.error("Failed to terminate contract");
        }
      },
    });
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Delete Hiring Record",
      icon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
      content: (
        <div>
          <p>Are you sure you want to delete this hiring record? This action cannot be undone.</p>
          <div style={{ marginTop: 12, padding: 12, background: "#fff2f0", borderRadius: 6 }}>
            <p style={{ margin: 0, fontWeight: 500 }}>{record.name}</p>
            <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>{record.id}</p>
          </div>
        </div>
      ),
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.delete(
            `${API_CONST.DELETE_HIRING_RECORD}/${record.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Hiring record deleted successfully");
          fetchHiringRecords();
        } catch (error) {
          console.error("Error deleting hiring record:", error);
          message.error("Failed to delete hiring record");
        }
      },
    });
  };

  const handleActivateHiring = (record) => {
    const { allDocsSigned, complianceSummary, id, name } = record;
    if (!allDocsSigned) {
      const signed = complianceSummary?.signed ?? 0;
      const total  = complianceSummary?.total  ?? 0;
      if (total === 0) {
        message.warning("Generate compliance documents first, then have the partner sign them before activating.");
      } else {
        message.warning(`${signed} of ${total} compliance document(s) signed. All must be signed before activation.`);
      }
      return;
    }
    Modal.confirm({
      title: "Activate Hiring Record",
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      content: (
        <div>
          <p>Activate the engagement for <strong>{name}</strong>?</p>
          <p style={{ color: "#555", fontSize: 13, marginTop: 8 }}>
            All compliance documents are signed. This will set the record to <strong>Active</strong>,
            update the talent's status to <strong>Hired</strong>, and send confirmation emails to all parties.
          </p>
        </div>
      ),
      okText: "Activate",
      okButtonProps: { style: { backgroundColor: "#52c41a", borderColor: "#52c41a" } },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.put(
            `${API_CONST.ACTIVATE_HIRING_RECORD}/${id}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Hiring record activated successfully");
          fetchHiringRecords();
        } catch (err) {
          message.error(err.response?.data?.message || "Failed to activate hiring record");
        }
      },
    });
  };

  const handleGenerateComplianceDocs = async (record) => {
    const hide = message.loading(`Generating compliance docs for ${record.id}…`, 0);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.post(
        `${API_CONST.COMPLIANCE_GENERATE}/${record.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      hide();
      if (res.data.created === 0) {
        message.info(res.data.message);
      } else {
        message.success(res.data.message);
      }
    } catch (err) {
      hide();
      message.error(err.response?.data?.error || "Failed to generate compliance documents");
    }
  };

  const handleBackfillAll = () => {
    Modal.confirm({
      title: "Backfill Compliance Docs for All Records",
      icon: <SyncOutlined style={{ color: "#1890ff" }} />,
      content: (
        <div>
          <p>This will create pending NDA, Contract, and Declaration documents for every hiring record that is missing them.</p>
          <p style={{ color: "#666", fontSize: 13, marginTop: 8 }}>Records that already have all documents are unaffected (idempotent).</p>
        </div>
      ),
      okText: "Run Backfill",
      cancelText: "Cancel",
      onOk: async () => {
        const hide = message.loading("Running compliance doc backfill…", 0);
        try {
          const token = localStorage.getItem("authToken");
          const res = await axios.post(
            API_CONST.COMPLIANCE_BACKFILL_ALL,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          hide();
          message.success(res.data.message);
          if (res.data.errors?.length) {
            console.warn("Backfill errors:", res.data.errors);
          }
        } catch (err) {
          hide();
          message.error(err.response?.data?.error || "Backfill failed");
        }
      },
    });
  };

  const getDaysLeftClass = (days) => {
    if (days < 0) return "inactive";
    if (days <= 30) return "critical";
    if (days <= 90) return "warning";
    return "safe";
  };

  const uniqueRoles = [...new Set(hiringRecords.map(r => r.role).filter(Boolean))];
  const uniqueLocations = [...new Set(hiringRecords.map(r => r.location).filter(Boolean))];

  const columns = [
    {
      title: "Hiring ID",
      dataIndex: "id",
      key: "id",
      width: "10%",
      render: (id) => (
        <span style={{ color: "#014c75", fontWeight: 500 }}>{id}</span>
      ),
    },
    {
      title: "Job Id",
      dataIndex: "jobId",
      key: "jobId",
      width: "10%",
      render: (jobId) => (
        <a
          href="#"
          style={{ color: "#1890ff", fontWeight: 500 }}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/admin/job-requirments/job-details/${jobId}`);
          }}
        >
          {jobId}
        </a>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "12%",
      render: (text, record) => (
        <a
          href="#"
          style={{ color: "#1890ff", fontWeight: 500 }}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/admin/talent-details/${record.talentId}`);
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: "14%",
      render: (text) => <span className="role-text">{text}</span>,
    },
    {
      title: "Experience",
      dataIndex: "experience",
      key: "experience",
      width: "6%",
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Partner Rate",
      dataIndex: "partnerRate",
      key: "partnerRate",
      width: "8%",
      render: (rate) => (
        <span className="rate-text">₹ {parseFloat(rate || 0).toLocaleString("en-IN")}</span>
      ),
    },
    {
      title: "Client Rate",
      dataIndex: "clientRate",
      key: "clientRate",
      width: "8%",
      render: (rate) => (
        <span className="rate-text">₹ {parseFloat(rate || 0).toLocaleString("en-IN")}</span>
      ),
    },
    {
      title: "Settled Cost",
      dataIndex: "settledCost",
      key: "settledCost",
      width: "8%",
      render: (rate) => (
        <span className="rate-text">₹ {parseFloat(rate || 0).toLocaleString("en-IN")}</span>
      ),
    },
    {
      title: "Gross Profit",
      key: "grossProfit",
      width: "8%",
      render: (_, record) => {
        const clientRate = parseFloat(record.clientRate) || 0;
        const settledCost = parseFloat(record.settledCost) || 0;
        const grossProfit = clientRate - settledCost;
        return (
          <span className="rate-text" style={{ fontWeight: 600, color: grossProfit >= 0 ? "#52c41a" : "#ff4d4f" }}>
            ₹ {grossProfit.toLocaleString("en-IN")}
          </span>
        );
      },
    },
    {
      title: "Total Billed",
      dataIndex: "totalBilled",
      key: "totalBilled",
      width: "8%",
      render: (amount) => (
        <span className="billed-text" style={{ fontWeight: 600, color: "#014c75" }}>
          ₹ {parseFloat(amount || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "Onboarding Date",
      dataIndex: "onboardingDate",
      key: "onboardingDate",
      width: "10%",
      render: (date) => (
        <span className="date-text">
          {date ? new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }) : '-'}
        </span>
      ),
    },
    {
      title: "Contract",
      dataIndex: "contractDuration",
      key: "contractDuration",
      width: "8%",
      render: (duration) => <span className="duration-text">{duration}</span>,
    },
    {
      title: "Days Left",
      dataIndex: "daysLeft",
      key: "daysLeft",
      width: "8%",
      align: "center",
      render: (days) => (
        <span className={`days-left ${getDaysLeftClass(days)}`}>
          {days < 0 ? "Completed" : `${days} days`}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: "8%",
      align: "center",
      render: (_, record) => (
        <div className="action-buttons">
          <Dropdown
            menu={{
              items: [
                {
                  key: "view",
                  label: "View Details",
                  icon: <EyeOutlined />,
                  onClick: () => navigate(`/admin/talents-hired/edit/${record.id}`),
                },
                {
                  key: "edit",
                  label: "Edit Record",
                  icon: <EditOutlined />,
                  onClick: () => navigate(`/admin/talents-hired/edit/${record.id}`),
                },
                {
                  key: "compliance",
                  label: "Generate Compliance Docs",
                  icon: <FileProtectOutlined />,
                  onClick: () => handleGenerateComplianceDocs(record),
                },
                ...(record.status === 'draft' ? [
                  {
                    key: "activate",
                    label: (
                      <Tooltip
                        title={
                          !record.allDocsSigned
                            ? record.complianceSummary?.total === 0
                              ? "Generate compliance docs first, then have the partner sign them"
                              : `${record.complianceSummary?.signed ?? 0}/${record.complianceSummary?.total ?? 0} compliance docs signed — all must be signed to activate`
                            : ""
                        }
                        placement="left"
                      >
                        <span>Activate Record</span>
                      </Tooltip>
                    ),
                    icon: <CheckCircleOutlined style={{ color: record.allDocsSigned ? "#52c41a" : "#bbb" }} />,
                    disabled: !record.allDocsSigned,
                    onClick: () => handleActivateHiring(record),
                  },
                ] : []),
                ...(record.status === 'active' ? [{
                  key: "terminate",
                  label: "Terminate Contract",
                  icon: <StopOutlined />,
                  danger: true,
                  onClick: () => handleTerminate(record),
                }] : []),
                {
                  type: "divider",
                },
                {
                  key: "delete",
                  label: "Delete Record",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => handleDelete(record),
                },
              ],
            }}
            trigger={["click"]}
          >
            <button className="icon-button menu-btn">
              <MoreOutlined />
            </button>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <TalentsHiredContainer>
      <PageHeader>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1>Talents Hired</h1>
            <p>Track hired talents and their assignments</p>
            {filterJobId && (
              <Tag 
                color="blue" 
                closable 
                onClose={clearJobIdFilter}
                style={{ marginLeft: 0, fontSize: 14, padding: "4px 12px" }}
              >
                Filtered by Job: {filterJobId}
              </Tag>
            )}
          </div>
          <Button
            icon={<SyncOutlined />}
            onClick={handleBackfillAll}
            style={{ marginTop: 4 }}
          >
            Backfill Compliance Docs
          </Button>
        </div>
      </PageHeader>

      <MetricsContainer>
        <Card className="metric-card">
          <div className="metric-content">
            <Avatar
              size={64}
              icon={<DollarOutlined />}
              className="metric-icon"
              style={{ backgroundColor: "#e6fff9" }}
            />
            <div className="metric-info">
              <h3>{formatCurrency(metrics.activeMonthlyBilling)}</h3>
              <p>Active Monthly Billing</p>
            </div>
          </div>
        </Card>
        <Card className="metric-card">
          <div className="metric-content">
            <Avatar
              size={64}
              icon={<TrophyOutlined />}
              className="metric-icon"
              style={{ backgroundColor: "#f6ffed" }}
            />
            <div className="metric-info">
              <h3>{formatCurrency(metrics.totalBilled)}</h3>
              <p>Total Billed</p>
            </div>
          </div>
        </Card>
        <Card className="metric-card">
          <div className="metric-content">
            <Avatar
              size={64}
              icon={<LineChartOutlined />}
              className="metric-icon"
              style={{ backgroundColor: "#fff7e6" }}
            />
            <div className="metric-info">
              <h3>{formatCurrency(metrics.avgPerTalent)}</h3>
              <p>Avg per Talent</p>
            </div>
          </div>
        </Card>
      </MetricsContainer>

      <TabsContainer>
        <Button
          className={activeTab === "Active Talents" ? "tab-button active" : "tab-button"}
          onClick={() => setActiveTab("Active Talents")}
        >
          Active ({activeTalents.length})
        </Button>
        <Button
          className={activeTab === "Pending Activation" ? "tab-button active" : "tab-button"}
          onClick={() => setActiveTab("Pending Activation")}
          style={activeTab !== "Pending Activation" && draftTalents.length > 0 ? { position: "relative" } : {}}
        >
          Pending Activation ({draftTalents.length})
          {draftTalents.length > 0 && activeTab !== "Pending Activation" && (
            <span style={{ position: "absolute", top: -4, right: -4, background: "#fa8c16", color: "#fff", borderRadius: "50%", width: 16, height: 16, fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
              {draftTalents.length > 9 ? "9+" : draftTalents.length}
            </span>
          )}
        </Button>
        <Button
          className={activeTab === "Inactive Talents" ? "tab-button active" : "tab-button"}
          onClick={() => setActiveTab("Inactive Talents")}
        >
          Inactive ({inactiveTalents.length})
        </Button>
      </TabsContainer>

      <Flex align="start" justify="space-between" style={{ marginBottom: "20px" }}>
        <Flex gap="middle">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search by name or role"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            placeholder={
              <span>
                <FilterOutlined style={{ marginRight: 8 }} />
                Filter by Role
              </span>
            }
            value={filterRole}
            onChange={setFilterRole}
            style={{ width: 220 }}
            allowClear
          >
            {uniqueRoles.map(role => (
              <Option key={role} value={role}>{role}</Option>
            ))}
          </Select>
          <Select
            placeholder={
              <span>
                <FilterOutlined style={{ marginRight: 8 }} />
                Filter by Location
              </span>
            }
            value={filterLocation}
            onChange={setFilterLocation}
            style={{ width: 200 }}
            allowClear
          >
            {uniqueLocations.map(loc => (
              <Option key={loc} value={loc}>{loc}</Option>
            ))}
          </Select>
        </Flex>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/admin/talents-hired/add")}
          style={{
            backgroundColor: "#00d9a9",
            borderColor: "#00d9a9",
            height: "40px",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          Add Hiring Record
        </Button>
      </Flex>

      <TableContainer>
        <Spin spinning={loading}>
          {currentData.length > 0 ? (
            <Table
              columns={columns}
              dataSource={currentData}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (total) => `Total ${total} talents`,
              }}
              scroll={{ x: 1600 }}
            />
          ) : (
            <EmptyState>
              <Empty description={loading ? "Loading..." : `No ${activeTab.toLowerCase()} found`} />
            </EmptyState>
          )}
        </Spin>
      </TableContainer>
    </TalentsHiredContainer>
  );
};

export default TalentsHired;
